import { IAppointment, IPractitioner } from "lobbie";
import { useCallback, useMemo, useState } from "react";
import { FiExternalLink } from "react-icons/fi";
import Avatar from "src/components/shared/Avatar";
import { BROWSER_TIME_ZONE, DATE_TIME_FORMATS } from "src/constants";
import { useCurrentPatientRedux } from "src/hooks/patients/useLobbiePatient";
import { logDev, toAddressString } from "src/utils";
import { daysjsFromUnix } from "src/utils/datetimes";
import AppointmentCalendarICS from "./AppointmentCalendarICS";

interface IProps {
    appointment: IAppointment;
}

const avatarStyle = { width: 50, height: 50 };

const TopRow = ({
    appointment: { accountPatient, practitioner, startUnix, timeZone },
}: {
    appointment: IAppointment;
}) => {
    const patient = useCurrentPatientRedux();

    const startDayjs = useMemo(() => daysjsFromUnix(startUnix, timeZone), [startUnix, timeZone]);
    const [source, setSource] = useState<string>(practitioner?.profilePictureSignedS3Url || "");

    const handleError = useCallback(() => setSource(""), []);

    const getPractitionerFirstName = useCallback((p: IPractitioner) => {
        if (p.title) {
            return `${p.title} ${p.firstName}`;
        }
        return p.firstName;
    }, []);

    const getName = useCallback(() => {
        if (accountPatient.name === patient?.name) {
            return `${patient.name} (you)`;
        }
        return accountPatient.name;
    }, [accountPatient.name, patient?.name]);

    return (
        <div className="row d-flex align-items-center">
            <div className="col-3 py-3 text-center">
                <div className="row">
                    <div className="col">
                        <span className="body-large bold">
                            {!startDayjs
                                ? ""
                                : startDayjs
                                      .format(DATE_TIME_FORMATS.Appointment.PatientHomeDateMonth)
                                      .toUpperCase()}
                        </span>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <h2>
                            {!startDayjs
                                ? ""
                                : startDayjs
                                      .format(DATE_TIME_FORMATS.Appointment.PatientHomeDateDay)
                                      .toUpperCase()}
                        </h2>
                        <span className="bold">
                            {!startDayjs
                                ? ""
                                : startDayjs
                                      .tz(BROWSER_TIME_ZONE)
                                      .format(DATE_TIME_FORMATS.Appointment.PatientHomeTime)}
                        </span>
                    </div>
                </div>
            </div>
            <div className="col-9 py-3 border-left">
                <div className="row">
                    <div className="col-3">
                        {source ? (
                            <Avatar
                                onError={handleError}
                                src={source}
                                alt={practitioner.name}
                                style={avatarStyle}
                            />
                        ) : (
                            <Avatar
                                src="/doctor_avatar.png"
                                style={avatarStyle}
                                alt={practitioner.name}
                            />
                        )}
                    </div>
                    <div className="col-9 text-right">
                        <span className="badge rounded-pill text-bg-primary lg body-xsmall">
                            {getName()}
                        </span>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <div className="provider-name">
                            <h4>
                                {getPractitionerFirstName(practitioner)}
                                <br />
                                {practitioner.lastName}
                            </h4>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <span className="bold body-small gray2">{practitioner.position || ""}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AppointmentListItem: React.FC<IProps> = ({ appointment }) => {
    const { location, practitioner } = appointment;

    const openMap = useCallback(() => {
        if (appointment.isTelehealth) {
            if (practitioner.telehealthUrl) {
                const opened = window.open(practitioner.telehealthUrl, "_blank");
                opened?.focus();
            }
        } else {
            const address = toAddressString(location.address);
            if (!address) return;

            const opened = window.open(`https://maps.google.com/?q=${address}`, "_blank");
            opened?.focus();
        }
    }, [location.address, appointment.isTelehealth, practitioner.telehealthUrl]);

    if (!location || !practitioner) {
        logDev(
            "AppointmentListItem - appointment has no location or no practitioner - return null component",
        );
        return null;
    }

    return (
        <div className="row border rounded cy-patient-appointment-list-item">
            <div className="col">
                <TopRow appointment={appointment} />
                <div className="row border-top bg-primary-light">
                    <div className="col-3 text-center py-3">
                        <AppointmentCalendarICS appointmentId={appointment.id} />
                    </div>
                    <div className="col-9 border-left">
                        <div className="row bg-primary-light">
                            <div className="col py-3">
                                <div className="row">
                                    <div className="col bold gray2">
                                        <div>{location.name}</div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div
                                        className="col mt-2 d-flex align-items-center pointer blue bold"
                                        onClick={openMap}
                                    >
                                        {appointment.isTelehealth
                                            ? "Telehealth"
                                            : toAddressString(location.address)}
                                        &nbsp;
                                        <FiExternalLink size={"1.25em"} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentListItem;
